import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ArticleCard as ArticleCardPayload } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { publicApi, ApiError } from '@/lib/api-client';
import { queryKeys, STALE } from '@/lib/query';
import { BOOKMARKS_ENABLED, trackArticleView, trackShare } from '@/lib/analytics';
import { useArticleEngagement } from '@/lib/use-engagement';
import { absoluteUrl } from '@/lib/dom';
import { ArticleBody, ArticleHeader, ReadingProgress, ShareBar } from '@/components/article';
import { ArticleCard } from '@/components/content';
import { VideoCard } from '@/components/video';
import { AdSlot } from '@/components/home';
import { Icon } from '@/components/shell';
import { Button, ErrorState } from '@/components/ui';
import { ArticleSkeleton, Section } from './page-parts';

/**
 * The article page — PRD §14, and the page readers actually spend their time on.
 *
 * Both §1.6 URL shapes route here. The server has usually resolved the article already
 * (`ssr-data.ts` calls the Phase 6 service in-process and seeds the query cache), so on
 * a first visit this `useQuery` finds the data it needs and renders no skeleton at all;
 * the loading and error states below are for client-side navigation.
 *
 * A renamed slug never reaches this component: the server answers 301 before rendering
 * (§1.6, ADR-012), because a client-side redirect would return 200 for a URL that no
 * longer exists and keep it indexed.
 */
export default function ArticlePage() {
  const { locale, t, path, setAlternates } = useLocale();
  const otherLocale = locale === 'as' ? 'en' : 'as';
  const params = useParams();

  const segments = {
    category: params['categorySlug'] ?? '',
    ...(params['districtSlug'] ? { district: params['districtSlug'] } : {}),
    slug: params['slug'] ?? '',
  };
  const routePath = path(
    ...[segments.category, segments.district, segments.slug].filter((value): value is string =>
      Boolean(value),
    ),
  );

  const {
    data: article,
    isPending,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.article(locale, routePath),
    queryFn: () => publicApi.article(locale, segments),
    staleTime: STALE.article,
  });

  // Fires once per session per article, after the reader has stayed (see `analytics.ts`).
  // The cleanup is what makes "stayed" true: navigating away inside the delay cancels it.
  useEffect(() => {
    if (!article) return undefined;
    if (article.translation) {
      setAlternates({
        [article.translation.locale]: article.translation.path,
        [locale]: routePath,
      });
    } else {
      setAlternates({
        [otherLocale]: `/${otherLocale}/${article.category.slug}`,
        [locale]: routePath,
      });
    }
    const cleanupTracking = trackArticleView(article.id, routePath);
    return () => {
      setAlternates(null);
      cleanupTracking?.();
    };
  }, [article, locale, otherLocale, routePath, setAlternates]);

  // Scroll milestones while they read, active reading time when they leave (Phase 16).
  useArticleEngagement(article?.id, routePath);

  if (isError) {
    if (error instanceof ApiError && error.status === 404) {
      return (
        <div className="mx-auto max-w-2xl px-gutter py-16 text-center">
          <div className="rounded-xl border border-line bg-soft/40 p-8 shadow-sm">
            <span className="text-4xl" role="img" aria-label="Newspaper">📰</span>
            <h1 className="mt-4 text-2xl font-extrabold text-ink">
              {locale === 'en' ? 'Article Not Found in English' : 'বাতৰিটো অসমীয়াত উপলব্ধ নহয়'}
            </h1>
            <p className="mt-2 text-sm text-muted">
              {locale === 'en'
                ? 'This article may be available in Assamese or has been moved.'
                : 'এই বাতৰিটো ইংৰাজীত উপলব্ধ হ’ব পাৰে বা স্থানান্তৰ কৰা হৈছে।'}
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <a
                href={`/${otherLocale}`}
                className="inline-flex items-center gap-1.5 rounded-md bg-red px-4 py-2 text-sm font-bold text-white transition hover:bg-red/90"
              >
                {locale === 'en' ? 'Go to Assamese Edition' : 'ইংৰাজী সংস্কৰণলৈ যাওক'}
              </a>
              {segments.category && (
                <a
                  href={`/${locale}/${segments.category}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-line bg-white px-4 py-2 text-sm font-semibold text-ink transition hover:border-red hover:text-red"
                >
                  {locale === 'en' ? 'Browse Category' : 'শ্ৰেণী চাওক'}
                </a>
              )}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="px-gutter py-16">
        <ErrorState
          title={t('error.genericTitle')}
          body={t('error.genericBody')}
          retryLabel={t('action.retry')}
          onRetry={() => void refetch()}
        />
      </div>
    );
  }

  if (isPending || !article) {
    return <ArticleSkeleton label={t('status.loading')} />;
  }

  const shareUrl = absoluteUrl(article.path);
  // Which network, not just that something was shared: "most shared" is a headline
  // figure, but where a story travels is what tells a desk where to put the next one.
  const share = (network: string) => trackShare(article.id, routePath, network);

  return (
    <article>
      <ReadingProgress />
      <ArticleHeader article={article} />

      <div className="mx-auto max-w-3xl px-gutter pb-4 pt-6">
        <ShareBar url={shareUrl} title={article.headline} onShare={share} />
      </div>

      <div className="mx-auto max-w-3xl px-gutter">
        <ArticleBody html={article.contentHtml} />

        {article.tags.length > 0 && (
          <ul className="mt-8 flex flex-wrap gap-2" data-print-hide>
            {article.tags.map((tag) => (
              <li key={tag.id}>
                <a
                  href={tag.path}
                  className="inline-block rounded-md border border-line px-3 py-1.5 text-xs font-semibold text-body transition hover:border-red hover:text-red"
                >
                  #{tag.name}
                </a>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-line pt-5">
          <ShareBar url={shareUrl} title={article.headline} onShare={share} />
          {/* PRD §3.1's "if enabled": the control exists so the layout is final, and the
              flag stays off until Phase 19 gives it somewhere to save to. */}
          {BOOKMARKS_ENABLED && (
            <Button variant="secondary" size="sm" icon={<Icon name="bookmark" size={14} />}>
              {t('action.bookmark')}
            </Button>
          )}
        </div>
      </div>

      <InArticleAd />

      {/* PRD §13: the clips an editor attached to this story, in the same card the
          homepage and `/videos` use (Phase 12). */}
      {article.related.videos.length > 0 && (
        // `data-print-hide` on a wrapper, because `Section` takes no DOM props — and a
        // grid of video thumbnails is the first thing that should not reach paper.
        <div data-print-hide>
          <Section title={t('section.videos')} className="pt-0">
            <div className="grid grid-cols-1 gap-4.5 sm:grid-cols-2 lg:grid-cols-4">
              {article.related.videos.slice(0, 4).map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </Section>
        </div>
      )}

      <RelatedContent
        sameCategory={article.related.sameCategory}
        sameAuthor={article.related.sameAuthor}
        trending={article.related.trending}
      />
    </article>
  );
}

/**
 * PRD §17's in-article placement, at its reserved height (Phase 8's `AdSlot` does the
 * reserving). Phase 13 fills it; the box is the same size either way, so nothing moves
 * under a reader mid-sentence when it does.
 */
function InArticleAd() {
  const { locale } = useLocale();
  const { data } = useQuery({
    queryKey: queryKeys.homepage(locale),
    queryFn: () => publicApi.homepage(locale),
    staleTime: STALE.homepage,
  });

  const slot = data?.ads.find((ad) => ad.placement === 'in-article-banner');
  if (!slot) return null;

  return (
    <div className="mx-auto max-w-3xl px-gutter py-8" data-print-hide>
      <AdSlot slot={slot} />
    </div>
  );
}

/**
 * Related content, all four rails from the Phase 6 payload — same category, same author,
 * trending. Rendered only where there is something to show: an empty "More from this
 * reporter" heading is worse than no heading.
 */
function RelatedContent({
  sameCategory,
  sameAuthor,
  trending,
}: {
  sameCategory: ArticleCardPayload[];
  sameAuthor: ArticleCardPayload[];
  trending: ArticleCardPayload[];
}) {
  const { t } = useLocale();

  const rails: { key: string; title: string; articles: ArticleCardPayload[] }[] = [
    { key: 'related', title: t('article.related'), articles: sameCategory },
    { key: 'author', title: t('article.moreFromAuthor'), articles: sameAuthor },
    { key: 'trending', title: t('section.trending'), articles: trending },
  ].filter((rail) => rail.articles.length > 0);

  if (rails.length === 0) return null;

  return (
    <div data-print-hide>
      {rails.map((rail) => (
        <Section key={rail.key} title={rail.title}>
          <div className="grid grid-cols-1 gap-4.5 sm:grid-cols-2 lg:grid-cols-4">
            {rail.articles.slice(0, 4).map((related) => (
              <ArticleCard key={related.id} article={related} />
            ))}
          </div>
        </Section>
      ))}
    </div>
  );
}
